import { useRef, useState } from 'react'
import { SlidersHorizontal } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { useMediaQuery } from '@/hooks/use-media-query'
import { cn } from '@/lib/utils'
import {
  sanitizeRangeInput,
  type MultiSelections,
  type RangeSelection,
  type RangeSelections,
  type ShopifyFilterGroup,
} from './search-state'
import {
  activeFilterCount,
  displayFilterGroupLabel,
  filterGroupBadge,
  formatFilterOptionLabel,
  formatMatchingProductsCopy,
  formatProductSearchCount,
  formatRangeDisplayValue,
  formatRangeUnit,
  numberFromRangeValue,
  optionAvailability,
  usableUniqueFilterOptions,
} from './search-filters'
import { SearchInput } from '@/components/search-input'

/** Below Tailwind's lg breakpoint the product page stacks; the filters then live in a sheet. */
const STACKED_QUERY = '(max-width: 1023px)'

export function FilterPanel({
  groups,
  isLoading,
  isUpdating,
  isError,
  total,
  multiSelections,
  rangeSelections,
  onToggleOption,
  onRangeChange,
  onClearGroup,
  onClearAll,
}: {
  groups: ShopifyFilterGroup[]
  isLoading: boolean
  isUpdating: boolean
  isError: boolean
  total?: number
  multiSelections: MultiSelections
  rangeSelections: RangeSelections
  onToggleOption: (key: string, value: string) => void
  onRangeChange: (key: string, bound: 'min' | 'max', value: string) => void
  onClearGroup: (key: string) => void
  onClearAll: () => void
}) {
  const selectedCount = activeFilterCount(multiSelections, rangeSelections)
  // Below the desktop layout the panel would sit above the results and push them around as it
  // loads; there it is a Filters button that opens a bottom sheet instead (USA-1536).
  const compact = useMediaQuery(STACKED_QUERY)
  const [sheetOpen, setSheetOpen] = useState(false)
  const sheetRef = useRef<HTMLDivElement>(null)
  const status = isLoading
    ? 'Loading contextual options…'
    : isUpdating
      ? 'Updating options…'
      : formatMatchingProductsCopy(total)

  const options = (
    <>
      {isError && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 p-2 text-xs text-destructive">
          Filter options could not load. Try changing your search or clearing filters.
        </div>
      )}
      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, idx) => (
            <Skeleton key={idx} className="h-14 w-full" />
          ))}
        </div>
      ) : groups.length ? (
        <div className={cn('space-y-2 transition-opacity', isUpdating && 'opacity-70')}>
          {groups.map((group) => (
            <FilterGroup
              key={group.key}
              group={group}
              selectedValues={multiSelections[group.key] ?? []}
              selectedRange={rangeSelections[group.key] ?? {}}
              disabled={isUpdating}
              onToggleOption={onToggleOption}
              onRangeChange={onRangeChange}
              onClearGroup={onClearGroup}
            />
          ))}
        </div>
      ) : (
        <p className="rounded-md bg-muted px-2 py-1.5 text-xs text-foreground">
          No contextual filters available.
        </p>
      )}
    </>
  )

  if (compact) {
    return (
      <>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="h-9"
            aria-label={selectedCount ? `Filters, ${selectedCount} on` : 'Filters'}
            onClick={() => setSheetOpen(true)}
          >
            <SlidersHorizontal className="h-4 w-4" /> Filters
            {selectedCount > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 px-1.5 tabular-nums">
                {selectedCount}
              </Badge>
            )}
          </Button>
          {selectedCount > 0 && (
            <Button variant="ghost" size="sm" className="h-9 px-2 text-xs" onClick={onClearAll}>
              Clear all
            </Button>
          )}
        </div>
        <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
          <SheetContent
            ref={sheetRef}
            side="bottom"
            // Focus the sheet, not a filter's search box: the keyboard opens only when one is tapped.
            onOpenAutoFocus={(event) => {
              event.preventDefault()
              sheetRef.current?.focus()
            }}
            className="flex max-h-[85dvh] flex-col gap-0 rounded-t-xl p-0 pb-[env(safe-area-inset-bottom)] outline-none"
          >
            <SheetHeader className="shrink-0 space-y-0.5 px-4 pb-3 pt-4 pr-12 text-left">
              <SheetTitle className="text-base">Filters</SheetTitle>
              <SheetDescription className="text-xs" aria-live="polite">
                {status}
              </SheetDescription>
            </SheetHeader>
            <div className="min-h-0 flex-1 space-y-2 overflow-y-auto overscroll-contain px-4 pb-3">
              {options}
            </div>
            <div className="flex shrink-0 gap-2 border-t px-4 py-3">
              {selectedCount > 0 && (
                <Button variant="outline" className="h-11 flex-1" onClick={onClearAll}>
                  Clear all
                </Button>
              )}
              <Button className="h-11 flex-1" onClick={() => setSheetOpen(false)}>
                Done
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      </>
    )
  }

  return (
    <Card className="flex flex-col gap-0 overflow-hidden py-3 lg:sticky lg:top-4 lg:max-h-[calc(100vh-2rem)]">
      <CardHeader className="shrink-0 px-3 pb-2">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <SlidersHorizontal className="h-4 w-4" /> Filters
            </CardTitle>
            <CardDescription className="text-xs" aria-live="polite">
              {status}
            </CardDescription>
          </div>
          {selectedCount > 0 && (
            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={onClearAll}>
              Clear all
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="min-h-0 flex-1 space-y-2 overflow-y-auto px-3 pb-3">
        {options}
      </CardContent>
    </Card>
  )
}

function DualRangeSlider({
  group,
  selectedRange,
  disabled,
  onRangeChange,
}: {
  group: ShopifyFilterGroup
  selectedRange: RangeSelection
  disabled: boolean
  onRangeChange: (key: string, bound: 'min' | 'max', value: string) => void
}) {
  const minBound = numberFromRangeValue(group.min)
  const maxBound = numberFromRangeValue(group.max)
  if (minBound == null || maxBound == null || minBound >= maxBound) return null

  const selectedMin = numberFromRangeValue(selectedRange.min) ?? minBound
  const selectedMax = numberFromRangeValue(selectedRange.max) ?? maxBound
  const lower = Math.min(
    Math.max(selectedMin, minBound),
    Math.max(Math.min(selectedMax, maxBound), minBound),
  )
  const upper = Math.max(Math.min(selectedMax, maxBound), lower)
  const span = maxBound - minBound
  const minPercent = ((lower - minBound) / span) * 100
  const maxPercent = ((upper - minBound) / span) * 100
  const step = span <= 10 ? '0.01' : span <= 100 ? '0.1' : '1'
  const formatSliderValue = (value: number, bound: 'min' | 'max') => {
    if (bound === 'min' && value === minBound) return ''
    if (bound === 'max' && value === maxBound) return ''
    return String(value)
  }

  return (
    <div className="space-y-1 pt-1">
      <div className="relative h-6" aria-label={`${displayFilterGroupLabel(group)} range slider`}>
        <div className="absolute left-0 right-0 top-1/2 h-1 -translate-y-1/2 rounded-full bg-muted" />
        <div
          className="absolute top-1/2 h-1 -translate-y-1/2 rounded-full bg-primary"
          style={{ left: `${minPercent}%`, right: `${100 - maxPercent}%` }}
        />
        <input
          type="range"
          min={minBound}
          max={maxBound}
          step={step}
          value={lower}
          disabled={disabled}
          aria-label={`${displayFilterGroupLabel(group)} minimum`}
          onChange={(event) => {
            const next = Math.min(Number(event.target.value), upper)
            onRangeChange(group.key, 'min', formatSliderValue(next, 'min'))
          }}
          className="pointer-events-none absolute inset-x-0 top-1/2 z-10 h-0 w-full -translate-y-1/2 appearance-none bg-transparent accent-primary disabled:opacity-60 [&::-moz-range-thumb]:pointer-events-auto [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border [&::-moz-range-thumb]:border-border [&::-moz-range-thumb]:bg-background [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border [&::-webkit-slider-thumb]:border-border [&::-webkit-slider-thumb]:bg-background [&::-webkit-slider-thumb]:shadow-sm"
        />
        <input
          type="range"
          min={minBound}
          max={maxBound}
          step={step}
          value={upper}
          disabled={disabled}
          aria-label={`${displayFilterGroupLabel(group)} maximum`}
          onChange={(event) => {
            const next = Math.max(Number(event.target.value), lower)
            onRangeChange(group.key, 'max', formatSliderValue(next, 'max'))
          }}
          className="pointer-events-none absolute inset-x-0 top-1/2 z-20 h-0 w-full -translate-y-1/2 appearance-none bg-transparent accent-primary disabled:opacity-60 [&::-moz-range-thumb]:pointer-events-auto [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border [&::-moz-range-thumb]:border-border [&::-moz-range-thumb]:bg-background [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border [&::-webkit-slider-thumb]:border-border [&::-webkit-slider-thumb]:bg-background [&::-webkit-slider-thumb]:shadow-sm"
        />
      </div>
      <div className="flex justify-between text-[10px] text-muted-foreground">
        <span>{formatRangeDisplayValue(group, group.min, { includeUnit: true })}</span>
        <span>{formatRangeDisplayValue(group, group.max, { includeUnit: true })}</span>
      </div>
    </div>
  )
}

function FilterGroup({
  group,
  selectedValues,
  selectedRange,
  disabled,
  onToggleOption,
  onRangeChange,
  onClearGroup,
}: {
  group: ShopifyFilterGroup
  selectedValues: string[]
  selectedRange: RangeSelection
  disabled: boolean
  onToggleOption: (key: string, value: string) => void
  onRangeChange: (key: string, bound: 'min' | 'max', value: string) => void
  onClearGroup: (key: string) => void
}) {
  const [optionSearch, setOptionSearch] = useState('')
  const options = usableUniqueFilterOptions(group)
  const displayLabel = displayFilterGroupLabel(group)
  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(optionSearch.toLowerCase()),
  )
  const hasSelection = selectedValues.length > 0 || selectedRange.min || selectedRange.max
  const badge = filterGroupBadge(group)

  return (
    <section
      className="space-y-2 rounded-lg border bg-card/50 p-2"
      aria-label={`${displayLabel} filter`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 space-y-1">
          <div className="flex min-w-0 flex-wrap items-center gap-1.5">
            <h3 className="truncate text-xs font-semibold uppercase tracking-wide text-foreground">
              {displayLabel}
            </h3>
            {badge && (
              <Badge variant="secondary" className="h-4 shrink-0 px-1 py-0 text-[10px] font-medium">
                {badge}
              </Badge>
            )}
          </div>
          {group.unit && (
            <p className="text-[11px] leading-none text-muted-foreground">
              {formatRangeUnit(group.unit)}
            </p>
          )}
        </div>
        {hasSelection && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 shrink-0 px-1.5 text-[11px]"
            disabled={disabled}
            onClick={() => onClearGroup(group.key)}
          >
            Clear
          </Button>
        )}
      </div>

      {group.type === 'range' ? (
        <div className="space-y-1.5">
          <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-1.5">
            <SearchInput
              type="text"
              inputMode="decimal"
              placeholder="Min"
              value={
                selectedRange.min != null && selectedRange.min !== ''
                  ? formatRangeDisplayValue(group, selectedRange.min)
                  : formatRangeDisplayValue(group, group.min)
              }
              disabled={disabled}
              aria-label={`${displayLabel} minimum`}
              onFocus={(event) => event.currentTarget.select()}
              onChange={(event) =>
                onRangeChange(group.key, 'min', sanitizeRangeInput(event.target.value))
              }
              className="h-8 px-2 text-xs"
            />
            <span className="text-xs text-muted-foreground">to</span>
            <SearchInput
              type="text"
              inputMode="decimal"
              placeholder="Max"
              value={
                selectedRange.max != null && selectedRange.max !== ''
                  ? formatRangeDisplayValue(group, selectedRange.max)
                  : formatRangeDisplayValue(group, group.max)
              }
              disabled={disabled}
              aria-label={`${displayLabel} maximum`}
              onFocus={(event) => event.currentTarget.select()}
              onChange={(event) =>
                onRangeChange(group.key, 'max', sanitizeRangeInput(event.target.value))
              }
              className="h-8 px-2 text-xs"
            />
          </div>
          <DualRangeSlider
            group={group}
            selectedRange={selectedRange}
            disabled={disabled}
            onRangeChange={onRangeChange}
          />
        </div>
      ) : (
        <div className="space-y-1.5">
          {options.length > 8 && (
            <SearchInput
              value={optionSearch}
              onChange={(e) => setOptionSearch(e.target.value)}
              placeholder={`Search ${displayLabel.toLowerCase()}…`}
              disabled={disabled}
              className="h-7 px-2 text-xs"
            />
          )}
          <div className="max-h-40 space-y-0.5 overflow-auto pr-1">
            {filteredOptions.length ? (
              filteredOptions.map((option) => {
                const checked = selectedValues.includes(option.value)
                const availability = optionAvailability(option)
                const unavailable = availability === 0 && !checked
                return (
                  <label
                    key={option.value}
                    className={cn(
                      'flex cursor-pointer items-center gap-1.5 rounded-md px-1.5 py-1 text-xs leading-tight hover:bg-muted',
                      checked && 'bg-primary/10 text-primary hover:bg-primary/10',
                      unavailable &&
                        'cursor-not-allowed text-muted-foreground/60 opacity-65 hover:bg-transparent',
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={disabled || unavailable}
                      aria-label={
                        unavailable
                          ? `${option.label}, unavailable with current filters`
                          : option.label
                      }
                      onChange={() => onToggleOption(group.key, option.value)}
                      className="h-3.5 w-3.5 shrink-0 rounded border-border accent-primary disabled:cursor-not-allowed"
                    />
                    <span className="min-w-0 flex-1 truncate">
                      {formatFilterOptionLabel(option, group)}
                    </span>
                    {availability != null && (
                      <span
                        className={cn(
                          'rounded bg-muted px-1 text-[10px] text-muted-foreground',
                          unavailable && 'bg-muted/60 text-muted-foreground/70',
                        )}
                        title={
                          option.available_count != null
                            ? `${formatProductSearchCount(option.available_count)} products available with current filters`
                            : undefined
                        }
                      >
                        {formatProductSearchCount(availability)}
                      </span>
                    )}
                  </label>
                )
              })
            ) : (
              <p className="rounded-md bg-muted px-2 py-1.5 text-xs text-muted-foreground">
                No options in this context.
              </p>
            )}
          </div>
        </div>
      )}
    </section>
  )
}
